<template>
    <div
        :class="[
            $style[vertical ? 'vertical' : 'horizontal'],
            fullWidth ? $style['full-width'] : '',
            $style['media-gallery'],
        ]"
    >
        <div :class="[$style['image-frame-container']]">
            <image-arrow
                mode="prev"
                v-if="showArrows"
                @click="previousImage()"
            ></image-arrow>
            <div
                :class="[
                    $style['image-frame'],
                    expandEnabled ? $style['image-frame--expand-enabled'] : '',
                ]"
                @click="expandIfEnabled($event)"
                @keydown.enter="expandIfEnabled($event)"
                :tabindex="expandEnabled ? '0' : undefined"
                :aria-expanded="
                    expandEnabled ? `${hasAnotherViewOpen}` : undefined
                "
                ref="frame"
            >
                <div :class="$style['expand-overlay']" v-if="expandEnabled">
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="64"
                        height="64"
                        fill="currentColor"
                        viewBox="-1 -1 17 17"
                    >
                        <path
                            fill-rule="evenodd"
                            d="M6.5 12a5.5 5.5 0 1 0 0-11 5.5 5.5 0 0 0 0 11M13 6.5a6.5 6.5 0 1 1-13 0 6.5 6.5 0 0 1 13 0"
                            stroke="currentColor"
                            stroke-width="1px"
                        />
                        <path
                            d="M10.344 11.742q.044.06.098.115l3.85 3.85a1 1 0 0 0 1.415-1.414l-3.85-3.85a1 1 0 0 0-.115-.1 6.5 6.5 0 0 1-1.398 1.4z"
                        />
                        <path
                            fill-rule="evenodd"
                            d="M6.5 3a.5.5 0 0 1 .5.5V6h2.5a.5.5 0 0 1 0 1H7v2.5a.5.5 0 0 1-1 0V7H3.5a.5.5 0 0 1 0-1H6V3.5a.5.5 0 0 1 .5-.5"
                        />
                    </svg>
                </div>
                <media-gallery-view
                    :expand-enabled="expandEnabled"
                    @expand="emit('expand')"
                    key="null"
                    :item="undefined"
                    v-show="selectedItem == null"
                    :selected="selectedItem == null"
                >
                </media-gallery-view>
                <template v-for="item in mappedData">
                    <media-gallery-view
                        :expand-enabled="expandEnabled"
                        @expand="emit('expand')"
                        v-if="item.selectable !== false"
                        :key="item.key"
                        :lazy="item.key > 0 && item.firstSelectable == false"
                        :item="item"
                        v-show="selectedItem == item"
                        :selected="selectedItem == item"
                    >
                        <template
                            #after-image
                            v-if="item.firstSelectable === true"
                        >
                            <slot name="after-first-image"></slot>
                        </template>
                    </media-gallery-view>
                </template>
                <slot name="inside-frame"></slot>
            </div>
            <slot name="under-image"></slot>
            <image-arrow
                mode="next"
                v-if="showArrows"
                @click="nextImage()"
            ></image-arrow>
        </div>
        <div
            :style="thumbnailContainerStyle"
            :class="$style['thumbnail-container']"
        >
            <ul
                :class="$style['thumbnails']"
                :style="thumbnailListStyle"
                v-drag-by-mouse
                ref="thumbnailList"
            >
                <template v-for="item in mappedData" :key="item.key">
                    <li
                        v-no-whitespaces
                        v-if="item !== mappedData[0]"
                        :class="$style['thumbnail-spacer']"
                        :style="thumbnailSpacerStyle"
                    ></li>
                    <li
                        v-no-whitespaces
                        :class="[
                            $style['thumbnail-item'],
                            item === selectedItem ? $style['selected'] : '',
                        ]"
                        ref="thumbnailItems"
                        :style="thumbnailItemStyle"
                        @click="selectImage(item, $event.target)"
                    >
                        <media-gallery-image
                            :value="
                                item.thumbnail != null
                                    ? typeof item.thumbnail == 'string'
                                        ? item.thumbnail
                                        : item.thumbnail.src
                                    : fallbackImageUrl
                            "
                            :size="
                                item.thumbnail != null &&
                                typeof item.thumbnail != 'string'
                                    ? item.thumbnail.size
                                    : undefined
                            "
                            :sources="
                                item.thumbnail != null &&
                                typeof item.thumbnail != 'string'
                                    ? item.thumbnail.sources
                                    : undefined
                            "
                            :alt="
                                item.thumbnail != null &&
                                typeof item.thumbnail != 'string'
                                    ? item.thumbnail.alt
                                    : undefined
                            "
                            :srcset="
                                item.thumbnail != null &&
                                typeof item.thumbnail != 'string'
                                    ? item.thumbnail.srcset
                                    : undefined
                            "
                        ></media-gallery-image>
                    </li>
                </template>
            </ul>
        </div>
    </div>
</template>

<style module lang="scss">
@import "../../../../responsive/style.scss";

.media-gallery {
    width: 100%;
}
.media-gallery.vertical {
    display: flex;
    flex-direction: row-reverse;
}
.media-gallery.horizontal {
    display: flex;
    flex-direction: column;
}

.media-gallery.full-width {
    align-self: stretch;
}

.image-frame-container {
    flex: 1;
    // width:100%;
    height: 100%;
    position: relative;
}

.image-frame {
    box-sizing: border-box;
    border: 1px solid #d8d8d8;
    border-radius: 4px;
    padding: 10px;
    display: inline-block;
    width: 100%;
    // height:100%;
    position: relative;

    &--expand-enabled {
        cursor: pointer;

        .expand-overlay {
            margin: 11px;
            position: absolute;
            box-shadow: 0px 0px 0px transparent;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            background-color: transparent;
            z-index: 1;
            pointer-events: none;
            display: flex;
            align-items: center;
            justify-content: center;
            opacity: 0;
            transition: opacity 0.4s, background-color 0.4s, box-shadow 0.4s,
                backdrop-filter 0.4s;
            color: white;
            backdrop-filter: blur(0px);
        }

        &:hover,
        &:focus,
        &:focus-visible {
            outline: none;
            .expand-overlay {
                box-shadow: 0px 0px 6px black;
                background-color: rgba(0, 0, 0, 0.4);
                opacity: 1;
                backdrop-filter: blur(2px);
            }
        }
    }
}

.image-frame :global(.view-media) {
    height: auto;
}

.media-gallery:not(.full-width) .image-frame :global(.view-media) {
    max-width: 380px;
}

.media-gallery.full-width .image-frame :global(.view-media) {
    min-width: 100%;
    max-width: 100%;
}

.thumbnail-container {
    position: relative;
}

.thumbnails {
    position: absolute;
    display: block;
    list-style: none;
    padding: 0;
    margin: 0;
    -ms-overflow-style: none; /* hide scrollbar for Internet Explorer, Edge */
    scrollbar-width: none; /* hide scrollbar for Firefox */
    scroll-behavior: smooth;
}

.thumbnails:global(.dragging) {
    scroll-snap-type: none !important;
    scroll-behavior: auto;
}

.thumbnails::-webkit-scrollbar {
    display: none; /* hide scrollbar for Chrome, Safari, and Opera */
}

.horizontal .thumbnails {
    overflow-x: scroll;
    overflow-y: hidden;
    top: 0;
    width: 100%;
    white-space: nowrap;
    scroll-snap-type: x proximity;
}

.vertical .thumbnails {
    overflow-x: hidden;
    overflow-y: scroll;
    left: 0;
    height: 100%;
    scroll-snap-type: y proximity;
}

.thumbnail-spacer {
    text-align: center;
    list-style: none;
    margin: 0;
    padding: 0;
    display: inline-block;
}

.horizontal .thumbnail-spacer {
    display: inline-block;
}

.vertical .thumbnail-spacer {
    display: block;
}

.thumbnail-item {
    text-align: center;
    list-style: none;
    box-sizing: border-box;
    border: 1px solid #d8d8d8;
    border-radius: 4px;
    overflow: hidden;
    scroll-snap-align: start;
    cursor: pointer;
}

.thumbnail-item img {
    width: 100%;
    height: 100%;
    transition: transform 400ms cubic-bezier(0, 0, 0, 1);
    transform: scale(1);
}

.thumbnail-item.selected img {
    transform: scale(1.45);
}

.horizontal .thumbnail-item {
    display: inline-block;
}

.vertical .thumbnail-item {
    display: block;
}
</style>

<script lang="ts">
export default {
    name: "media-gallery",
};
</script>

<script setup lang="ts">
import mediaGalleryImage from "./common/media-gallery-image.vue";
//import mediaGalleryTransition from "./media-gallery-transition.vue";
import {
    computed,
    type ObjectDirective,
    onMounted,
    ref,
    watch,
    type PropType,
    onBeforeUnmount,
} from "vue";
import { getBreakpointObservable } from "../../../../shared/breakpoint-observable";
import mediaGalleryView from "./big-frame/media-gallery-view.vue";
import { type MediaBase, type MediaItem } from "./types";
import fastdom from "fastdom";
import { debounce } from "lodash";
import ImageArrow from "./big-frame/image-arrow.vue";

type ParsedItem = { key: number; firstSelectable: boolean } & MediaItem;

function isEmptyText(sibling: Node): boolean;
function isEmptyText(sibling: null | undefined): false;
function isEmptyText(sibling: Node | null | undefined): boolean {
    if (sibling != null) {
        if (
            sibling.nodeType === sibling.TEXT_NODE &&
            (sibling.textContent?.trim() ?? "") === ""
        ) {
            return true;
        }
    }
    return false;
}

const vDragByMouse: ObjectDirective<HTMLElement> = {
    mounted: function (el) {
        el.addEventListener("dragstart", function (dragStartEvent) {
            //console.log("dragStart");
            dragStartEvent.preventDefault();
            el.classList.add("dragging");
            const initialX = dragStartEvent.clientX;
            const initialY = dragStartEvent.clientY;
            const initialScrollTop = el.scrollTop;
            const initialScrollLeft = el.scrollLeft;

            const setPos = function (
                event: PointerEvent | MouseEvent | DragEvent
            ) {
                const newValueTop =
                    initialScrollTop + (initialY - event.clientY);
                const newValueLeft =
                    initialScrollLeft + (initialX - event.clientX);
                el.scrollTop = newValueTop;
                el.scrollLeft = newValueLeft;
            };

            const endListeners: {
                name: string;
                func: EventListenerOrEventListenerObject;
            }[] = [];

            const moveHandleEventName = "mousemove";

            const endTracking = function () {
                el.removeEventListener(moveHandleEventName, dragHandler);
                el.classList.remove("dragging");
                for (const item of endListeners) {
                    el.removeEventListener(item.name, item.func);
                }
            };

            el.addEventListener(moveHandleEventName, dragHandler);

            const mouseUpEventHandler = function (dragEndEvent: DragEvent) {
                endTracking();
                setPos(dragEndEvent);
            } as EventListener;
            endListeners.push({
                name: "mouseup",
                func: mouseUpEventHandler,
            });
            el.addEventListener("mouseup", mouseUpEventHandler, {
                once: true,
            });

            function dragHandler(dragEvent: Event) {
                dragEvent.preventDefault();

                if ((dragEvent as DragEvent).buttons == 0) {
                    endTracking();
                    return;
                }

                setPos(dragEvent as DragEvent);
            }
        });
    },
};
const vNoWhitespaces: ObjectDirective<HTMLElement> = {
    mounted: function (el) {
        if (el.nextSibling != null && isEmptyText(el.nextSibling)) {
            el.nextSibling.remove();
        }
        if (el.previousSibling != null && isEmptyText(el.previousSibling)) {
            el.previousSibling.remove();
        }
    },
};
const props = defineProps({
    value: {
        type: Array as PropType<MediaItem[]>,
        required: true,
    },
    fullWidth: {
        type: Boolean,
        default: false,
    },
    vertical: {
        type: Boolean,
        default: false,
    },
    expandEnabled: Boolean,
    hasAnotherViewOpen: Boolean,
    showArrows: Boolean,
    enableArrowKeys: Boolean,
});

const emit = defineEmits(["expand", "close"]);

const mappedData = ref(createMappedData(props.value));
const selectedIndex = ref<undefined | number>(0);
const fallbackImageUrl = "data:image/gif;base64,R0lGODlhAQABAAAAADs=";
const thumbnailContainerWidthValue = ref<number>();
const thumbnailContainerHeightValue = ref<number>();
const breakpointObserver = getBreakpointObservable();
const currentBreakpoint = computed(() => {
    return breakpointObserver.currentBreakpoint;
});

function expandIfEnabled(event: Event) {
    if (!props.expandEnabled) {
        event.preventDefault();
        return;
    }
    emit("expand");
}

const itemSize = computed(() => {
    let width = 108;
    let height = 81;

    if (currentBreakpoint.value != null && currentBreakpoint.value.up("lg")) {
        width = 100;
        height = 75;
    }

    return {
        width,
        height,
    };
});

const selectedItem = computed(() => {
    if (mappedData.value == null) return null;
    if (selectedIndex.value == null) return null;
    return mappedData.value[selectedIndex.value];
});
// const thumbnailContainerWidth = computed(() => {
//     if (thumbnailContainerWidthValue.value == null)
//         return thumbnailContainerWidthValue.value;
//     return thumbnailContainerWidthValue.value + "px";
// });
// const thumbnailContainerHeight = computed(() => {
//     if (thumbnailContainerHeightValue.value == null)
//         return thumbnailContainerHeightValue.value;
//     return thumbnailContainerHeightValue.value + "px";
// });
const thumbnailContainerStyle = computed(() => {
    if (props.vertical) {
        return {
            "margin-right": spacerSize.value + "px",
            width: itemSize.value.width + "px",
        };
    } else {
        return {
            "margin-top": spacerSize.value + "px",
            height: itemSize.value.height + "px",
        };
    }
});
const thumbnailListStyle = computed(() => {
    return {};
});
const spacerSize = computed(() => {
    let limit: number;
    let itemSiz: number;
    if (props.vertical) {
        limit = thumbnailContainerHeightValue.value ?? 0;
        itemSiz = itemSize.value.height;
    } else {
        limit = thumbnailContainerWidthValue.value ?? 0;
        itemSiz = itemSize.value.width;
    }

    const division = limit / itemSiz;
    const itemsToShow = Math.floor(division);
    const remainder = division % 1;
    let spacerSize = (remainder * itemSiz) / (itemsToShow - 1);

    if (spacerSize < 5) spacerSize = 5;
    if (spacerSize > 15) spacerSize = 15;
    return spacerSize;
});
const thumbnailSpacerStyle = computed(() => {
    if (props.vertical) {
        return { width: "", height: spacerSize.value + "px" };
    } else {
        return { width: spacerSize.value + "px", height: "" };
    }
});
const thumbnailItemStyle = computed(() => {
    return {
        height: itemSize.value.height + "px",
        width: itemSize.value.width + "px",
    };
});

onMounted(() => {
    window.addEventListener("resize", () => {
        resizeEventHandler(/* "window resize" */);
    });
    updateSizing(/* "mounted" */);
});

function resizeEventHandler(/* source */) {
    updateSizing(/* source */);
}
const deboncedSetSizeForContainer = debounce(() => {
    fastdom.measure(() => {
        setSizeForThumbnailContainer();
    });
});
function updateSizing() {
    //console.log("UPDATE SIZING FROM "+src+" "+ performance.now());
    deboncedSetSizeForContainer();
}
const frame = ref<HTMLElement>();
function setSizeForThumbnailContainer() {
    let width = thumbnailContainerWidthValue.value;
    let height = thumbnailContainerHeightValue.value;

    const fr = frame.value;
    if (fr == null || !(fr instanceof HTMLElement)) throw new Error("no frame");
    const frameBoundRect = fr.getBoundingClientRect();
    if (props.vertical) {
        width = undefined;
        height = frameBoundRect.height;
    } else {
        width = frameBoundRect.width;
        height = undefined;
    }

    thumbnailContainerWidthValue.value = width;
    thumbnailContainerHeightValue.value = height;

    //console.log("NEW SIZE",width,height);
}

const thumbnailList = ref<HTMLElement>();
const thumbnailItems = ref<HTMLElement[]>();

function previousImage() {
    relImage(-1);
}

function nextImage() {
    relImage(1);
}

function relImage(rel: number) {
    if (selectedIndex.value == null) return;
    if (mappedData.value == null) return;
    const newIndex =
        (mappedData.value.length + selectedIndex.value + rel) %
        mappedData.value.length;
    selectImage(mappedData.value[newIndex], thumbnailItems.value?.[newIndex]);
}

function selectImage(
    item: ParsedItem,
    element: EventTarget | undefined | null = null
) {
    if (item.selectable === false) return;
    if (element != null && element instanceof HTMLElement) {
        const thumbList = thumbnailList.value;
        if (thumbList == null || !(thumbList instanceof HTMLElement))
            throw new Error("missing thumbnail list");
        if (props.vertical) {
            thumbList.scrollTop =
                element.offsetTop +
                thumbList.offsetHeight / 2 +
                element.offsetHeight / 2 -
                thumbList.offsetHeight;
            //element.scrollIntoView({ behaviour: "smooth", block: "center", inline: "nearest" });
        } else {
            thumbList.scrollLeft =
                element.offsetLeft +
                thumbList.offsetWidth / 2 +
                element.offsetWidth / 2 -
                thumbList.offsetWidth;
            //element.scrollIntoView({ behaviour: "smooth", block: "nearest", inline: "center" });
        }
    }
    selectedIndex.value = mappedData.value.indexOf(item);
}
function createMappedData(data: MediaItem[]): ParsedItem[] {
    if (data == null) return data;
    const firstSelectable = data.findIndex((x) => x.selectable !== false);
    return data.map((x, i) =>
        Object.assign({}, x, {
            key: i,
            firstSelectable: firstSelectable === i,
            thumbnail: getThumbnailForItem(x),
        })
    );
}
function getThumbnailForItem(item: MediaItem) {
    let thumbnail = null;
    if (item.thumbnail != null) thumbnail = item.thumbnail;
    else thumbnail = item;
    if (thumbnail == null) return null;
    if (
        typeof thumbnail === "string" ||
        ("type" in thumbnail && thumbnail.type === "picture")
    )
        return thumbnail;
    return null;
}
function getNewIndex(mapData: MediaBase[] | undefined | null, index: number) {
    if (mapData == null) return;
    if (index < 0) index = 0;
    if (mapData.length == 0) index = 0;
    else if (index >= mapData.length) index = mapData.length - 1;
    while (mapData[index] != null && mapData[index].selectable === false) {
        index++;
        if (mapData[index] == null) {
            index = -1;
            break;
        }
    }
    return index;
}

watch(
    () => props.value,
    (newValue) => {
        mappedData.value = createMappedData(newValue);
    }
);

watch(
    () => mappedData.value,
    (newValue) => {
        selectedIndex.value = getNewIndex(newValue, selectedIndex.value ?? 0);
    },
    { immediate: true }
);

const boundArrowKeys = ref(false);

function globalKeyHandler(ev: KeyboardEvent) {
    if (ev.ctrlKey || ev.metaKey || ev.shiftKey) {
        return;
    }
    if (ev.key === "ArrowLeft" || ev.key == "ArrowUp") {
        previousImage();
        ev.preventDefault();
    } else if (ev.key === "ArrowRight" || ev.key == "ArrowDown") {
        nextImage();
        ev.preventDefault();
    } else if (ev.key === "Escape") {
        emit("close");
    } else {
        console.log(ev.key);
    }
}

function bindKeyEvents(bind: boolean) {
    if (bind && !boundArrowKeys.value) {
        window.addEventListener("keydown", globalKeyHandler);
        boundArrowKeys.value = bind;
    } else if (!bind && boundArrowKeys.value) {
        window.removeEventListener("keydown", globalKeyHandler);
        boundArrowKeys.value = bind;
    }
}

watch(
    () => props.enableArrowKeys,
    (newValue) => {
        console.log("bindKeyEvents(" + newValue + ")");
        bindKeyEvents(newValue);
    },
    { immediate: true }
);
onBeforeUnmount(() => {
    bindKeyEvents(false);
});
</script>
